import PlateHeader from './m1nt/PlateHeader'
import { looksLikeImageUrl } from './CardArtifactsGrid'

const SOURCE_STYLES = {
  owner: {
    label: 'Owner',
    icon: 'ri-user-3-line',
    text: 'text-m1-amber',
    border: 'border-m1-amber/40',
    tint: 'bg-m1-amber/10',
  },
  grader: {
    label: 'Grader',
    icon: 'ri-search-eye-line',
    text: 'text-m1-ice',
    border: 'border-m1-ice/40',
    tint: 'bg-m1-ice/10',
  },
  lab: {
    label: 'M1NT Lab',
    icon: 'ri-flask-line',
    text: 'text-m1-violet',
    border: 'border-m1-violet/40',
    tint: 'bg-m1-violet/10',
  },
}

const TYPE_ICONS = {
  story: 'ri-book-open-line',
  file: 'ri-file-text-line',
  link: 'ri-links-line',
  certificate: 'ri-award-line',
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function extensionOf(url) {
  const path = String(url).split('?')[0]
  const match = path.match(/\.([a-z0-9]{2,5})$/i)
  return match ? match[1].toUpperCase() : null
}

function describe(item, href) {
  if (item.type === 'link') return hostOf(href)
  if (item.type === 'certificate') return extensionOf(href) ?? 'Certificate'
  if (item.type === 'story') return 'Story'
  return extensionOf(href) ?? 'File'
}

function iconFor(item, href) {
  if (item.type === 'file' && extensionOf(href) === 'PDF') return 'ri-file-pdf-2-line'
  return TYPE_ICONS[item.type] ?? 'ri-file-line'
}

function DocumentRow({ item, style }) {
  const href = item.href ?? item.storySrc
  if (!href) return null
  const thumb =
    item.type === 'certificate' && item.previewSrc && looksLikeImageUrl(item.previewSrc)
      ? item.previewSrc
      : null
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-white/[0.04] focus:outline-none focus-visible:bg-white/[0.06] sm:px-5"
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${style.border} ${style.tint} ${style.text}`}
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <i className={`${iconFor(item, href)} text-xl`} aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[16px] text-m1-ink">{item.label}</span>
        <span className="mt-0.5 block truncate font-m1-mono text-[12px] uppercase tracking-[0.08em] text-m1-muted">
          {describe(item, href)}
        </span>
      </span>
      <i
        className="ri-arrow-right-up-line shrink-0 text-lg text-m1-muted transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-m1-violet"
        aria-hidden="true"
      />
    </a>
  )
}

/**
 * Documents and links as a compact ledger: one panel per source (Owner / Grader / M1NT Lab),
 * one row per file. Replaces the tall preview tiles, which were mostly empty placeholders.
 */
export default function CardDocumentList({ groups }) {
  const visible = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.href ?? item.storySrc) }))
    .filter((group) => group.items.length > 0)
  if (visible.length === 0) return null

  const total = visible.reduce((sum, group) => sum + group.items.length, 0)
  const columns =
    visible.length >= 3 ? 'lg:grid-cols-3' : visible.length === 2 ? 'lg:grid-cols-2' : ''

  return (
    <div className="m1nt-plate w-full p-5 sm:p-7">
      <PlateHeader
        icon={<i className="ri-folder-open-line" />}
        eyebrow="Records"
        title={`${total} ${total === 1 ? 'document' : 'documents'} & links`}
        subtitle="Files and links from each stage, grouped by who added them."
      />
      <div className={`grid grid-cols-1 gap-4 ${columns}`}>
        {visible.map((group) => {
          const style = SOURCE_STYLES[group.source] ?? SOURCE_STYLES.owner
          return (
            <section
              key={group.source}
              className="min-w-0 overflow-hidden rounded-xl border border-m1-line-soft bg-black/20"
            >
              <header className="flex items-center justify-between gap-3 border-b border-m1-line-soft px-4 py-3 sm:px-5">
                <span className={`flex items-center gap-2 text-[14px] ${style.text}`}>
                  <i className={style.icon} aria-hidden="true" />
                  {style.label}
                </span>
                <span className="font-m1-mono text-[12px] tabular-nums text-m1-muted">
                  {String(group.items.length).padStart(2, '0')}
                </span>
              </header>
              <div className="divide-y divide-m1-line-soft">
                {group.items.map((item) => (
                  <DocumentRow key={item.key} item={item} style={style} />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}
